'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, User, ExternalLink } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';


interface LoginForm {
  username: string;
  password: string;
  remember: boolean;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setLoginError('');
    setIsSubmitting(true);
    // Backend integration: POST /api/auth/login — validate credentials against user database
    await new Promise((r) => setTimeout(r, 1200));
    if (data.username && data.password) {
      window.location.href = '/main-dashboard';
    } else {
      setLoginError('Invalid credentials. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-[55%] relative flex-col justify-between p-12 overflow-hidden bg-primary">
        {/* Background image */}
        <div className="absolute inset-0">
          <AppImage
            src="https://images.unsplash.com/photo-1733521262689-7842fd69f669"
            alt="Modern architecture building with clean geometric lines and glass facade against evening sky"
            fill
            priority
            sizes="55vw"
            className="object-cover opacity-30" />
          
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-primary/50" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-primary-foreground tracking-tight">DID</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Design Investigation & Development</p>
            <h1 className="text-display font-bold text-primary-foreground leading-tight">
              Your studio.<br />Your projects.<br />All in one place.
            </h1>
          </div>
          <p className="text-white text-sm leading-relaxed max-w-sm">
            Internal platform for DID — manage architecture and design projects from first pitch to final delivery.
          </p>
        </div>

        <div className="relative z-10">
          <a
            href="https://officedid.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-primary-foreground/40 hover:text-primary-foreground/70 transition-colors">
            
            <ExternalLink size={12} />
            officedid.com
          </a>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-background">
        <div className="w-full max-w-sm space-y-8">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <span className="text-xl font-bold text-foreground tracking-tight">DID</span>
          </div>

          {/* Header */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
            <p className="text-sm text-muted-foreground">Sign in to your DID workspace</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Error */}
            {loginError &&
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <p className="text-xs text-red-600 font-medium">{loginError}</p>
              </div>
            }

            {/* Username */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Email / Username</label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  {...register('username', { required: 'Username is required' })}
                  type="text"
                  className="input-base pl-10"
                  placeholder="name@officedid.com"
                  autoComplete="username" />
                
              </div>
              {errors.username && <p className="text-xs text-red-500 mt-1">{errors.username.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  {...register('password', { required: 'Password is required' })}
                  type={showPassword ? 'text' : 'password'}
                  className="input-base pl-10 pr-10"
                  placeholder="Your password"
                  autoComplete="current-password" />
                
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
            </div>

            {/* Remember me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  {...register('remember')}
                  type="checkbox"
                  className="w-4 h-4 rounded border-border text-accent focus:ring-ring" />
                
                <span className="text-xs text-muted-foreground">Remember me</span>
              </label>
              <button type="button" className="text-xs text-accent font-medium hover:underline">
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3 mt-2">
              
              {isSubmitting ?
              <>
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  <span>Signing in...</span>
                </> :

              'Sign In'
              }
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            Internal platform ·{' '}
            <a href="https://officedid.com" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
              officedid.com
            </a>
          </p>
        </div>
      </div>
    </div>);

}